import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Room, RoomFormData, roomService } from '../../../services/roomService';
import { ROOM_TYPE_LABELS } from '../timetableUtils';

interface RoomFormDialogProps {
  open: boolean;
  room: Room | null;
  onClose: () => void;
  onSaved: () => void;
}

const schema = yup.object({
  name: yup.string().trim().required('Give the room a name'),
  type: yup.string().required(),
  capacity: yup.number().min(1, 'At least 1 seat').required('How many seats?'),
});

const EMPTY: RoomFormData = { name: '', type: 'classroom', capacity: 40, description: '', facilities: [] };

/** Add or edit a room. */
const RoomFormDialog: React.FC<RoomFormDialogProps> = ({ open, room, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [facility, setFacility] = useState('');

  const formik = useFormik<RoomFormData>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (room) await roomService.updateRoom(room._id, values);
        else await roomService.createRoom(values);
        enqueueSnackbar(room ? 'Room updated' : 'Room added', { variant: 'success' });
        onSaved();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not save the room', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { resetForm } = formik;
  useEffect(() => {
    if (!open) return;
    setFacility('');
    resetForm({
      values: room
        ? { name: room.name, type: room.type, capacity: room.capacity, description: room.description || '', facilities: room.facilities || [] }
        : EMPTY,
    });
  }, [open, room, resetForm]);

  const addFacility = () => {
    const f = facility.trim();
    if (f && !formik.values.facilities?.includes(f)) formik.setFieldValue('facilities', [...(formik.values.facilities || []), f]);
    setFacility('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{room ? 'Edit room' : 'Add room'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name" name="name" autoFocus size="small"
              value={formik.values.name} onChange={formik.handleChange}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField select label="Kind" name="type" size="small" sx={{ flex: 1 }} value={formik.values.type} onChange={formik.handleChange}>
                {Object.entries(ROOM_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <TextField
                label="Seats" name="capacity" type="number" size="small" sx={{ width: 110 }}
                value={formik.values.capacity} onChange={formik.handleChange}
                error={formik.touched.capacity && !!formik.errors.capacity}
                helperText={formik.touched.capacity && formik.errors.capacity}
              />
            </Stack>
            <TextField label="Description" name="description" size="small" multiline minRows={2} value={formik.values.description} onChange={formik.handleChange} />
            <Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small" fullWidth placeholder="Facility, e.g. Projector"
                  value={facility} onChange={(e) => setFacility(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFacility(); } }}
                />
                <Button variant="outlined" onClick={addFacility} disabled={!facility.trim()}>Add</Button>
              </Stack>
              {!!formik.values.facilities?.length && (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {formik.values.facilities.map((f) => (
                    <Chip key={f} label={f} size="small" onDelete={() => formik.setFieldValue('facilities', formik.values.facilities?.filter((x) => x !== f))} />
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Labs and other special rooms are booked by the timetable for the lessons that need them.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">Cancel</Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>{room ? 'Save' : 'Add room'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RoomFormDialog;
